import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, Copy, Shuffle } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Generates a color palette using real color theory (complementary, analogous, triadic, and monochromatic relationships), from a base color you choose or a random starting point.`,
  how: `Pick a base color, choose a harmony mode, and set how many colors you want in the palette. Click any swatch to copy its hex code, or copy the whole palette at once.`,
  tip: `Complementary and triadic palettes tend to look best when one color dominates and the others are used as accents — using all of them in equal proportion often looks more chaotic than intentional. Analogous and monochromatic palettes are naturally more subtle and forgiving to use in equal amounts.`,
};

type HarmonyMode = "random" | "complementary" | "analogous" | "triadic" | "monochromatic";

const HARMONY_OPTIONS: { value: HarmonyMode; label: string }[] = [
  { value: "random", label: "Random" },
  { value: "complementary", label: "Complementary" },
  { value: "analogous", label: "Analogous" },
  { value: "triadic", label: "Triadic" },
  { value: "monochromatic", label: "Monochromatic" },
];

const COUNT_OPTIONS = [4, 5, 6, 8, 10];

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  h /= 360; s /= 100; l /= 100;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function randomColor(): string {
  return "#" + Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0");
}

/**
 * Builds a palette using actual color-harmony relationships in HSL space,
 * not independently-random hex values -- the tool's own description
 * claimed "complementary colors" and a base-color option before this,
 * neither of which the code actually did.
 */
function generatePalette(baseColor: string, mode: HarmonyMode, count: number): string[] {
  if (mode === "random") {
    return uniquify(Array.from({ length: count }, randomColor));
  }

  const [baseH, baseS, baseL] = hexToHsl(baseColor);
  const colors: string[] = [];

  if (mode === "complementary") {
    // Alternates between the base hue and its direct opposite (180°),
    // varying lightness for enough distinct swatches to fill the count.
    for (let i = 0; i < count; i++) {
      const isBase = i % 2 === 0;
      const hue = isBase ? baseH : baseH + 180;
      const lightness = Math.min(88, Math.max(15, baseL + (Math.floor(i / 2) - Math.floor(count / 4)) * 14));
      colors.push(hslToHex(hue, baseS, lightness));
    }
  } else if (mode === "analogous") {
    // Neighboring hues within ±60° of the base, evenly spaced -- colors
    // that naturally sit next to each other on the color wheel and read
    // as harmonious rather than clashing.
    const spread = 60;
    for (let i = 0; i < count; i++) {
      const offset = count > 1 ? (i / (count - 1) - 0.5) * spread : 0;
      colors.push(hslToHex(baseH + offset, baseS, baseL));
    }
  } else if (mode === "triadic") {
    // Three hues evenly spaced 120° apart (a triangle on the color
    // wheel), with lightness variation layered in to reach the full count.
    const triadHues = [baseH, baseH + 120, baseH + 240];
    for (let i = 0; i < count; i++) {
      const hue = triadHues[i % 3];
      const lightness = Math.min(85, Math.max(20, baseL + (Math.floor(i / 3) - 1) * 12));
      colors.push(hslToHex(hue, baseS, lightness));
    }
  } else if (mode === "monochromatic") {
    // Same hue and saturation throughout, stepping lightness from dark
    // to light -- a genuinely monochromatic palette, not just "same-ish" colors.
    for (let i = 0; i < count; i++) {
      const lightness = 15 + (i / Math.max(1, count - 1)) * 70;
      colors.push(hslToHex(baseH, baseS, lightness));
    }
  }

  return uniquify(colors);
}

/** Palettes must never show the same swatch twice (clamped lightness or grey
 * bases can collide): nudge a repeat's lightness until it is distinct. */
function uniquify(colors: string[]): string[] {
  const seen = new Set<string>();
  return colors.map(c => {
    let out = c;
    let step = 0;
    while (seen.has(out) && step < 30) {
      step++;
      const [h, sat, l] = hexToHsl(c);
      const delta = Math.ceil(step / 2) * 6 * (step % 2 ? 1 : -1);
      out = hslToHex(h, sat, Math.min(95, Math.max(5, l + delta)));
    }
    seen.add(out);
    return out;
  });
}

export default function ColorPalette() {
  const [baseColor, setBaseColor] = useState("#3366cc");
  const [mode, setMode] = useState<HarmonyMode>("complementary");
  const [count, setCount] = useState(6);
  const [colors, setColors] = useState<string[]>(() => generatePalette("#3366cc", "complementary", 6));

  const regenerate = () => setColors(generatePalette(baseColor, mode, count));
  const randomizeBase = () => {
    const newBase = randomColor();
    setBaseColor(newBase);
    setColors(generatePalette(newBase, mode, count));
  };
  const copyColor = (c: string) => { void copyText(c, `Copied ${c}`); };
  const copyAll = () => { void copyText(colors.join(", "), "Copied all colors!"); };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div>
          <Label className="text-xs">Base Color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="color"
              value={baseColor}
              onChange={e => { setBaseColor(e.target.value); setColors(generatePalette(e.target.value, mode, count)); }}
              className="h-9 w-9 rounded border cursor-pointer"
            />
            <span className="text-xs font-mono text-muted-foreground">{baseColor}</span>
          </div>
        </div>
        <div>
          <Label className="text-xs">Harmony</Label>
          <Select value={mode} onValueChange={(v) => { const m = v as HarmonyMode; setMode(m); setColors(generatePalette(baseColor, m, count)); }}>
            <SelectTrigger className="w-40 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{HARMONY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Colors</Label>
          <Select value={String(count)} onValueChange={(v) => { const c = Number(v); setCount(c); setColors(generatePalette(baseColor, mode, c)); }}>
            <SelectTrigger className="w-24 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNT_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={randomizeBase} className="gap-2 mb-0.5"><Shuffle className="h-4 w-4" /> Random Base</Button>
      </div>

      {blocked ? (
        <p className="text-sm text-center text-muted-foreground py-10 border rounded-xl">— blocked at today's usage limit —</p>
      ) : (
        <div className="flex rounded-xl overflow-hidden h-64 border">
          {colors.map((color, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end p-3 cursor-pointer transition-all hover:flex-[1.2]" style={{ backgroundColor: color }} onClick={() => copyColor(color)}>
              <span className="text-xs font-mono font-medium px-2 py-1 rounded bg-white/80 text-gray-800">{color}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3 mt-4">
        <Button onClick={regenerate} className="gap-2" disabled={blocked}><RefreshCw className="h-4 w-4" /> Generate New</Button>
        <Button variant="outline" onClick={copyAll} className="gap-2" disabled={blocked}><Copy className="h-4 w-4" /> Copy All</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">Click any color to copy its hex code.</p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
